/* eslint-disable no-mixed-operators */
/**
 * In-app ambient background — a calm clean-energy landscape.
 * Rolling misty hills, wind turbines turning slowly, a curved solar array with an
 * occasional light glint, a few swaying trees and clouds drifting by.
 *
 * Designed to stay out of the way of work:
 *  - slow, low-amplitude motion only (rotors ≈ 9–14 s per turn, sway ≤ 1°);
 *  - eases to ~20% speed while the user is typing or a field has focus (focus mode);
 *  - capped at 30 fps and DPR 1.5; pauses with the tab; still frame for reduced motion;
 *  - can be switched off entirely (setMotion(false)).
 *
 * createEnergyScene(canvas, { getAnchor, still }) → { setMotion(bool), setFocus(bool), destroy() }
 */
const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const hsl = (h, s, l, a = 1) => `hsla(${h},${s}%,${l}%,${a})`;
function mulberry(a) { return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

export function createEnergyScene(canvas, { getAnchor, still = false } = {}) {
  const ctx = canvas.getContext('2d');
  const reduce = still || matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 0, H = 0, DPR = 1, L = null, raf = 0, last = 0, t = 0, speed = 1, target = 1, motion = !reduce, destroyed = false, rt = 0;

  function layer(w, h) { const c = document.createElement('canvas'); c.width = Math.max(1, Math.ceil(w * DPR)); c.height = Math.max(1, Math.ceil(h * DPR)); const g = c.getContext('2d'); g.scale(DPR, DPR); return [c, g]; }
  function dots(g, arr) { arr.sort((a, b) => a.l - b.l); for (const d of arr) { g.fillStyle = hsl(d.h, d.s, d.l, d.a ?? 1); g.beginPath(); g.arc(d.x, d.y, d.r, 0, 6.283); g.fill(); } }

  /* a rounded broadleaf tree sprite */
  function tree(r, h, pale) {
    const w = h * 0.9, [c, g] = layer(w, h), bx = w / 2, tw = Math.max(1.4, h * 0.03);
    g.fillStyle = pale ? '#8d8270' : '#5a4634';
    g.beginPath(); g.moveTo(bx - tw, h); g.lineTo(bx - tw * 0.4, h * 0.45); g.lineTo(bx + tw * 0.4, h * 0.45); g.lineTo(bx + tw, h); g.fill();
    const blobs = [{ x: bx, y: h * 0.3, r: h * 0.2 }];
    for (let i = 0; i < 5; i++) { const a = (i / 5) * 6.283 + r(); blobs.push({ x: bx + Math.cos(a) * w * 0.22, y: h * 0.36 + Math.sin(a) * h * 0.14, r: h * (0.13 + r() * 0.06) }); }
    const H0 = 88 + r() * 26, S = pale ? 22 : 38, L0 = pale ? 58 : 36, dr = Math.max(1, h * 0.012), arr = [];
    for (const b of blobs) {
      g.fillStyle = hsl(H0, S, L0 - 8); g.beginPath(); g.arc(b.x, b.y, b.r * 0.85, 0, 6.283); g.fill();
      const n = Math.floor((b.r * b.r) / (dr * dr));
      for (let i = 0; i < n; i++) { const rr = Math.sqrt(r()) * b.r, a = r() * 6.283; const lit = (Math.cos(a) * 0.4 - Math.sin(a) * 0.9) * (rr / b.r); arr.push({ x: b.x + Math.cos(a) * rr, y: b.y + Math.sin(a) * rr, r: dr * (0.7 + r() * 0.8), h: H0 + (r() - 0.5) * 14, s: S, l: L0 + lit * 11 + (r() - 0.5) * 8 }); }
    }
    dots(g, arr);
    return { c, w, h };
  }

  function hill(g, x0, x1, base, amp, r, n = 6) {
    const pts = []; for (let i = 0; i <= n; i++) pts.push([x0 + (x1 - x0) * (i / n), base - amp * (0.5 + 0.5 * Math.sin(i * 1.3 + r() * 2)) - (i > 0 && i < n ? amp * 0.3 : 0)]);
    g.beginPath(); g.moveTo(x0, base + 400); g.lineTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) { const p = pts[i - 1], q = pts[i]; g.quadraticCurveTo(p[0], p[1], (p[0] + q[0]) / 2, (p[1] + q[1]) / 2); }
    g.lineTo(x1, pts[n][1]); g.lineTo(x1, base + 400); g.closePath();
    return pts;
  }

  function build() {
    DPR = Math.min(1.5, devicePixelRatio || 1);
    W = innerWidth; H = innerHeight;
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    const a = getAnchor ? getAnchor() : null;
    const cx = a && W >= 1000 ? a.left + a.width / 2 : W / 2;
    const s = clamp(Math.min(W / 1500, H / 900), 0.55, 1.25);  // scale for laptop ↔ desktop
    const r = mulberry(4417);
    const horizon = H * 0.6;

    /* far misty hills */
    const [far, fg] = layer(W, H);
    for (const [base, amp, col, al] of [[horizon, 70 * s, [176, 200, 180], 0.55], [horizon + 50 * s, 60 * s, [150, 186, 150], 0.6]]) {
      const gr = fg.createLinearGradient(0, base - amp * 1.4, 0, base + 160 * s);
      gr.addColorStop(0, `rgba(${col},${al})`); gr.addColorStop(1, `rgba(${col},0)`);
      fg.fillStyle = gr; hill(fg, -50, W + 50, base, amp, r, 7); fg.fill();
    }

    /* turbines: far (small, pale) and near */
    const turbines = [];
    const farX = [0.08, 0.22, 0.78, 0.93, 0.36, 0.64];
    farX.forEach((fx, i) => turbines.push({ x: fx * W + (r() - 0.5) * 40, y: horizon + 10 * s + r() * 30 * s, h: (120 + r() * 50) * s, alpha: 0.45, ph: r() * 6.28, sp: 0.07 + r() * 0.04, near: false, k: i }));
    [[cx - 470 * s, 1], [cx - 250 * s, 0.82], [cx + 300 * s, 0.9], [cx + 520 * s, 1.05]].forEach(([x, k]) => turbines.push({ x, y: horizon + 120 * s, h: 300 * s * k, alpha: 0.95, ph: r() * 6.28, sp: 0.075 + r() * 0.035, near: true }));

    /* mid hill (green dome) with foliage crest */
    const [mid, mg] = layer(W, H);
    const midBase = horizon + 150 * s;
    const gr = mg.createLinearGradient(0, midBase - 120 * s, 0, H);
    gr.addColorStop(0, '#b9d49a'); gr.addColorStop(0.35, '#9cc27c'); gr.addColorStop(1, '#7aa95f');
    mg.fillStyle = gr;
    mg.beginPath(); mg.moveTo(-50, H); mg.lineTo(-50, midBase + 40 * s);
    mg.bezierCurveTo(cx - 600 * s, midBase - 20 * s, cx - 300 * s, midBase - 110 * s, cx, midBase - 115 * s);
    mg.bezierCurveTo(cx + 300 * s, midBase - 110 * s, cx + 600 * s, midBase - 20 * s, W + 50, midBase + 40 * s);
    mg.lineTo(W + 50, H); mg.closePath(); mg.fill();
    const crest = [];
    for (let i = 0; i < W * 1.6; i++) {
      const x = r() * W, u = (x - cx) / (620 * s);
      const y = midBase - 115 * s * Math.max(0, 1 - u * u * 0.75) + 40 * s * Math.min(1, u * u) + r() * 26 * s;
      crest.push({ x, y, r: (1 + r() * 2) * s, h: 80 + r() * 35, s: 35 + r() * 15, l: 34 + r() * 18 });
    }
    dots(mg, crest);

    /* curved solar array */
    const [sol, sg] = layer(W, H);
    const SW = 560 * s, arcH = 30 * s, solBase = midBase - 70 * s, ph = 46 * s;
    const solPath = new Path2D();
    for (const row of [{ off: 0, k: 0.86, n: 11 }, { off: 46 * s, k: 1, n: 12 }]) {
      const n = row.n, w = SW * row.k;
      for (let i = 0; i < n; i++) {
        const u0 = i / n * 2 - 1, u1 = (i + 1) / n * 2 - 1, gap = 3 * s;
        const x0 = cx + u0 * w / 2 + gap / 2, x1 = cx + u1 * w / 2 - gap / 2;
        const y0 = solBase + row.off - (1 - u0 * u0) * arcH, y1 = solBase + row.off - (1 - u1 * u1) * arcH;
        const dx = -14 * s * (u0 + u1) / 2, h = ph * row.k;
        // legs
        sg.strokeStyle = 'rgba(236,240,238,.95)'; sg.lineWidth = 2 * s;
        sg.beginPath(); sg.moveTo(x0 + 4 * s, y0); sg.lineTo(x0 + 4 * s, y0 + 22 * s * row.k); sg.moveTo(x1 - 4 * s, y1); sg.lineTo(x1 - 4 * s, y1 + 22 * s * row.k); sg.stroke();
        const p = new Path2D(); p.moveTo(x0, y0); p.lineTo(x1, y1); p.lineTo(x1 + dx + 10 * s, y1 - h); p.lineTo(x0 + dx + 10 * s, y0 - h); p.closePath();
        const cg = sg.createLinearGradient(0, y0 - h, 0, y0);
        cg.addColorStop(0, '#6f8aa6'); cg.addColorStop(0.5, '#3d5875'); cg.addColorStop(1, '#2b4058');
        sg.fillStyle = cg; sg.fill(p);
        sg.strokeStyle = 'rgba(240,244,246,.95)'; sg.lineWidth = 1.6 * s; sg.stroke(p);
        sg.save(); sg.clip(p); sg.strokeStyle = 'rgba(255,255,255,.28)'; sg.lineWidth = 0.8 * s;
        for (let k = 1; k < 4; k++) { const f = k / 4; sg.beginPath(); sg.moveTo(x0 + (x1 - x0) * f, y0 + (y1 - y0) * f); sg.lineTo(x0 + (x1 - x0) * f + dx + 10 * s, y0 + (y1 - y0) * f - h); sg.stroke(); }
        for (let k = 1; k < 6; k++) { const f = k / 6; sg.beginPath(); sg.moveTo(x0 + (dx + 10 * s) * f, y0 - h * f); sg.lineTo(x1 + (dx + 10 * s) * f, y1 - h * f); sg.stroke(); }
        sg.restore();
        solPath.addPath(p);
      }
    }

    /* trees around the hill (sway) and front bushes */
    const trees = [];
    const spots = [-0.95, -0.82, -0.7, -0.56, 0.58, 0.7, 0.84, 0.97, -0.38, 0.4];
    spots.forEach((u) => {
      const h = (90 + r() * 90) * s * (Math.abs(u) > 0.6 ? 1.15 : 0.8);
      const x = cx + u * 640 * s;
      const base = midBase - 115 * s * Math.max(0, 1 - (u * 640 / 620) ** 2 * 0.75) + 40 * s * Math.min(1, (u * 640 / 620) ** 2) + 18 * s;
      trees.push({ ...tree(r, h, false), x, y: base, ph: r() * 6.28, f: 0.5 + r() * 0.4 });
    });
    const [front, frg] = layer(W, H);
    const fdots = [];
    for (let i = 0; i < 40; i++) {
      const x = r() * W, y = midBase + (20 + r() * 80) * s, br = (14 + r() * 26) * s;
      for (let k = 0; k < (br * br * 0.4) / (s * s); k++) { const a = r() * Math.PI + Math.PI, d = Math.sqrt(r()) * br; fdots.push({ x: x + Math.cos(a) * d * 1.4, y: y + Math.sin(a) * d, r: (1.2 + r() * 1.6) * s, h: 82 + r() * 35, s: 36 + r() * 12, l: 30 + -Math.sin(a) * 12 + r() * 10 }); }
    }
    dots(frg, fdots);

    /* fades: soft white edges so the landscape dissolves into the page */
    const [fade, fdg] = layer(W, H);
    const vg = fdg.createLinearGradient(0, 0, 0, H);
    vg.addColorStop(0, 'rgba(244,246,243,0)'); vg.addColorStop(0.55, 'rgba(244,246,243,0)'); vg.addColorStop(0.86, 'rgba(240,244,240,.55)'); vg.addColorStop(1, 'rgba(236,241,236,.92)');
    fdg.fillStyle = vg; fdg.fillRect(0, 0, W, H);
    for (const [x0, x1] of [[0, W * 0.12], [W, W * 0.88]]) { const hg = fdg.createLinearGradient(x0, 0, x1, 0); hg.addColorStop(0, 'rgba(244,246,243,.85)'); hg.addColorStop(1, 'rgba(244,246,243,0)'); fdg.fillStyle = hg; fdg.fillRect(Math.min(x0, x1), 0, Math.abs(x1 - x0), H); }

    /* clouds */
    const cr = mulberry(91);
    const clouds = Array.from({ length: 5 }, () => ({ x: cr() * W, y: H * (0.08 + cr() * 0.3), w: (180 + cr() * 220) * s, v: 3 + cr() * 4 }));

    L = { s, far, mid, sol, solPath, trees, turbines, front, fade, clouds };
  }

  function drawTurbine(tb, time) {
    const { x, y, h } = tb, tw = h * 0.028, hubY = y - h;
    ctx.save(); ctx.globalAlpha = tb.alpha;
    const g = ctx.createLinearGradient(x - tw, 0, x + tw, 0); g.addColorStop(0, '#e9eeec'); g.addColorStop(0.6, '#ffffff'); g.addColorStop(1, '#cdd6d2');
    ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(x - tw, y); ctx.lineTo(x - tw * 0.45, hubY); ctx.lineTo(x + tw * 0.45, hubY); ctx.lineTo(x + tw, y); ctx.fill();
    ctx.fillStyle = '#eef2f0'; ctx.beginPath(); ctx.ellipse(x + h * 0.012, hubY, h * 0.045, h * 0.016, 0, 0, 6.283); ctx.fill();
    const ang = tb.ph + time * tb.sp * 6.283, L0 = h * 0.5;
    for (let k = 0; k < 3; k++) {
      const a = ang + k * 2.0944;
      ctx.save(); ctx.translate(x, hubY); ctx.rotate(a);
      const bg = ctx.createLinearGradient(-h * 0.02, 0, h * 0.02, 0); bg.addColorStop(0, '#d6dddb'); bg.addColorStop(1, '#ffffff');
      ctx.fillStyle = bg; ctx.beginPath(); ctx.moveTo(-h * 0.012, 0); ctx.quadraticCurveTo(-h * 0.03, -L0 * 0.25, -h * 0.006, -L0); ctx.lineTo(h * 0.004, -L0); ctx.quadraticCurveTo(h * 0.014, -L0 * 0.3, h * 0.012, 0); ctx.closePath(); ctx.fill();
      ctx.restore();
    }
    ctx.fillStyle = '#f4f7f6'; ctx.beginPath(); ctx.arc(x, hubY, h * 0.014, 0, 6.283); ctx.fill();
    ctx.restore();
  }

  function render(dt) {
    if (!L) return;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, W, H);
    const { s } = L;
    // clouds
    for (const c of L.clouds) {
      c.x += c.v * dt; if (c.x - c.w > W) c.x = -c.w;
      const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.w * 0.5);
      g.addColorStop(0, 'rgba(255,255,255,.7)'); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.save(); ctx.translate(c.x, c.y); ctx.scale(1, 0.32); ctx.translate(-c.x, -c.y); ctx.fillRect(c.x - c.w, c.y - c.w, c.w * 2, c.w * 2); ctx.restore();
    }
    ctx.drawImage(L.far, 0, 0, W, H);
    for (const tb of L.turbines) if (!tb.near) drawTurbine(tb, t);
    for (const tb of L.turbines) if (tb.near) drawTurbine(tb, t);
    ctx.drawImage(L.mid, 0, 0, W, H);
    ctx.drawImage(L.sol, 0, 0, W, H);
    // light glint sweeping across the panels, briefly, every 14 s
    const cyc = (t % 14) / 14;
    if (cyc < 0.18) {
      const p = cyc / 0.18, gx = (W / 2 - 400 * s) + p * 800 * s;
      ctx.save(); ctx.clip(L.solPath);
      const g = ctx.createLinearGradient(gx - 60 * s, 0, gx + 60 * s, 0);
      g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.5, `rgba(255,255,255,${0.32 * Math.sin(p * Math.PI)})`); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.restore();
    }
    for (const tr of L.trees) {
      const sway = reduce ? 0 : Math.sin(t * tr.f + tr.ph) * 0.012;
      ctx.save(); ctx.translate(tr.x, tr.y); ctx.transform(1, 0, -sway, 1, 0, 0); ctx.drawImage(tr.c, -tr.w / 2, -tr.h, tr.w, tr.h); ctx.restore();
    }
    ctx.drawImage(L.front, 0, 0, W, H);
    ctx.drawImage(L.fade, 0, 0, W, H);
  }

  function loop(now) {
    if (destroyed) return;
    raf = requestAnimationFrame(loop);
    if (now - last < 33) return;                         // ~30 fps is plenty for slow ambient motion
    const dt = Math.min(0.1, (now - last) / 1000); last = now;
    speed += (target - speed) * Math.min(1, dt * 2.5);   // ease in/out of focus mode
    t += dt * speed;
    render(dt * speed);
  }

  const onResize = () => { clearTimeout(rt); rt = setTimeout(() => { build(); render(0); }, 120); };
  addEventListener('resize', onResize);
  build();
  t = 3.2; render(0);
  if (motion) { last = performance.now(); raf = requestAnimationFrame(loop); }

  return {
    setMotion(on) {
      if (reduce) return;
      motion = on; cancelAnimationFrame(raf);
      if (on) { last = performance.now(); raf = requestAnimationFrame(loop); }
    },
    setFocus(on) { target = on ? 0.2 : 1; },
    destroy() { destroyed = true; cancelAnimationFrame(raf); removeEventListener('resize', onResize); },
  };
}
